import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Lightning, ChartBar } from '@phosphor-icons/react';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * Compact usage badge showing "X / Y this month" with a progress bar.
 * Drops to nothing if the user has unlimited access on this counter.
 *
 * Props:
 *   counter   — usage key from /api/subscription/me usage object (e.g. "job_applications", "job_postings")
 *   label     — human label (e.g. "Applications")
 *   compact   — boolean; if true renders inline pill, else full card
 */
export const UsageBadge = ({ counter, label, compact = false }) => {
  const navigate = useNavigate();
  const { getRemaining, planId } = useSubscription();
  const info = getRemaining(counter);
  if (!info || info.limit === -1) return null;

  const pct = info.limit > 0 ? Math.min(100, Math.round((info.used / info.limit) * 100)) : 0;
  const danger = pct >= 80;
  const full = info.remaining <= 0;

  if (compact) {
    return (
      <button
        onClick={() => navigate('/pricing')}
        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-bold border-2 ${
          full ? 'bg-red-100 border-red-500 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            : danger ? 'bg-amber-100 border-amber-500 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-emerald-100 border-emerald-500 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        } transition-all hover:scale-105`}
        title={`Click to upgrade (${planId})`}
        data-testid={`usage-badge-${counter}`}
      >
        <ChartBar size={12} weight="bold" />
        {label}: {info.used}/{info.limit}
      </button>
    );
  }

  return (
    <div
      className="bg-white dark:bg-neutral-800 border-2 border-black rounded-lg p-3 shadow-[2px_2px_0px_#000]"
      data-testid={`usage-card-${counter}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-black uppercase tracking-wider text-neutral-700 dark:text-neutral-200">
          {label}
        </span>
        <span className={`text-xs font-bold ${full ? 'text-red-600' : danger ? 'text-amber-600' : 'text-neutral-500'}`}>
          {info.used} / {info.limit}
        </span>
      </div>
      <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${full ? 'bg-red-500' : danger ? 'bg-amber-500' : 'bg-emerald-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {(full || danger) && (
        <button
          onClick={() => navigate('/pricing')}
          className="mt-2 w-full h-9 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] text-xs font-black rounded-md border-2 border-black flex items-center justify-center gap-1.5 transition-all"
          data-testid={`upgrade-cta-${counter}`}
        >
          <Crown size={12} weight="fill" />
          {full ? 'Upgrade to Continue' : 'Upgrade for More'}
        </button>
      )}
    </div>
  );
};

/**
 * Full-screen upgrade prompt — shown when a 402 response comes back from the API.
 *
 * Props:
 *   open      — boolean
 *   onClose   — function
 *   limitInfo — the structured 402 detail body from the backend
 *               { feature, limit, used, plan_name, message, upgrade_url? }
 */
export const UpgradePrompt = ({ open, onClose, limitInfo }) => {
  const navigate = useNavigate();
  if (!open || !limitInfo) return null;

  const isStaticLimit = typeof limitInfo.limit === 'number' && limitInfo.limit >= 0;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="upgrade-prompt"
    >
      <div
        className="bg-white dark:bg-neutral-800 border-4 border-black rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-[8px_8px_0px_#3B82F6]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-16 h-16 bg-[#3B82F6] rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-black shadow-[4px_4px_0px_#000]">
          <Lightning size={32} weight="fill" className="text-white" />
        </div>

        <h2 className="text-2xl font-black text-neutral-900 dark:text-white text-center mb-2">
          {isStaticLimit ? 'Plan Limit Reached' : 'Premium Feature'}
        </h2>

        <p className="text-sm text-neutral-600 dark:text-neutral-300 text-center mb-4">
          {limitInfo.message || 'You\'ve reached the limit of your current plan.'}
        </p>

        {isStaticLimit && (
          <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg border-2 border-neutral-300 dark:border-neutral-700 p-3 mb-4">
            <div className="flex items-center justify-between text-xs font-bold text-neutral-600 dark:text-neutral-400 mb-1">
              <span>Used this period</span>
              <span data-testid="upgrade-prompt-usage">
                {limitInfo.used} / {limitInfo.limit}
              </span>
            </div>
            <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
              <div className="h-full bg-red-500" style={{ width: '100%' }} />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 transition-all"
            data-testid="upgrade-prompt-dismiss"
          >
            Maybe Later
          </button>
          <button
            onClick={() => { onClose(); navigate(limitInfo.upgrade_url || '/pricing'); }}
            className="flex-1 h-11 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center justify-center gap-2"
            data-testid="upgrade-prompt-cta"
          >
            <Crown size={16} weight="fill" />
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Convenience wrapper: parse an axios error and show the upgrade prompt
 * if the response was a 402 with our structured detail body.
 *
 * Returns true if it handled the error.
 */
export const handle402Error = (err, setUpgradeInfo) => {
  if (err?.response?.status === 402) {
    const detail = err.response.data?.detail;
    if (detail && typeof detail === 'object') {
      setUpgradeInfo(detail);
      return true;
    }
  }
  return false;
};

export default UpgradePrompt;
